<mat-card class="feature-surface">
	<mat-card-header>
		<div mat-card-avatar class="feature-surface__icon" [ngClass]="tone">
			<i class="material-icons" aria-hidden="true">call</i>
		</div>
		<mat-card-title>Collections</mat-card-title>
	</mat-card-header>
	<mat-card-content>

		<div *ngIf="loading" class="text-center my-4">
			<mat-progress-spinner diameter="32" mode="indeterminate" color="accent"></mat-progress-spinner>
		</div>

		<div *ngIf="!loading">
			<div *ngIf="!installed" class="feature-section mb-3">
				<p class="feature-section__title m-0">Note tracking not installed</p>
				<small class="text-muted">{{ installMessage }}</small>
			</div>

			<!-- Aging -->
			<div class="feature-tile-row" *ngIf="aging">
				<app-kpi-tile
					tone="accent"
					label="open balance"
					[value]="aging.balance | currency"
					[sub]="aging.open_docs + ' open document' + (aging.open_docs === 1 ? '' : 's')">
				</app-kpi-tile>
				<app-kpi-tile
					[tone]="overdue > 0 ? 'failed' : 'settled'"
					label="past due"
					[value]="overdue | currency"
					[sub]="aging.max_days ? aging.max_days + ' days on the oldest' : 'nothing past due'">
				</app-kpi-tile>
				<app-kpi-tile
					[tone]="aging.b_90plus > 0 ? 'failed' : 'default'"
					label="90+ days"
					[value]="aging.b_90plus | currency"
					[sub]="aging.oldest_due ? ('oldest due ' + (aging.oldest_due | date:'MMM d, y')) : null">
				</app-kpi-tile>
				<app-kpi-tile
					*ngIf="installed"
					[tone]="promisedOpen > 0 ? 'partial' : 'default'"
					label="promised"
					[value]="promisedOpen | currency"
					[sub]="brokenPromises.length ? (brokenPromises.length + ' promise(s) already broken') : 'on open promises'">
				</app-kpi-tile>
				<app-kpi-tile
					*ngIf="installed"
					[tone]="nextFollowup && nextFollowup.followup_date < today ? 'failed' : 'default'"
					label="next follow-up"
					[value]="nextFollowup ? (nextFollowup.followup_date | date:'MMM d') : '—'"
					[sub]="nextFollowup ? label(outcomes, nextFollowup.outcome) : 'nothing scheduled'">
				</app-kpi-tile>
			</div>

			<div class="d-flex justify-content-between align-items-center mt-4 mb-2">
				<div>
					<span class="text-muted" *ngIf="customer">
						Terms {{ customer.paymenttermsname || customer.paymentterms }}
						· limit {{ customer.creditlimit | currency }}
						<span *ngIf="customer.salesmanname"> · rep {{ customer.salesmanname }}</span>
						<span *ngIf="customer.phoneno"> · <a [href]="telHref(customer.phoneno)" matTooltip="Call this number">{{ customer.phoneno }}</a></span>
						<span *ngIf="customer.email"> · <a [href]="'mailto:' + customer.email" matTooltip="Email — opens a draft">{{ customer.email }}</a></span>
					</span>
				</div>
				<button mat-flat-button color="accent" [disabled]="!installed" (click)="openSheet()">
					<i class="material-icons" aria-hidden="true">call</i>
					Log contact
				</button>
			</div>

			<!-- Collection trail -->
			<div class="feature-section">
				<p class="feature-section__title">Collection history</p>
				<table class="feature-table feature-table--responsive">
					<thead>
						<tr>
							<th>Date</th>
							<th>Contact</th>
							<th>Outcome</th>
							<th>Promised</th>
							<th>Follow-up</th>
							<th>Doc</th>
							<th>Note</th>
							<th>By</th>
							<th>Status</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let n of notes">
							<td data-label="Date">{{ n.contact_date | date:'MMM d, y' }}</td>
							<td data-label="Contact">
								<span>{{ label(contactTypes, n.contact_type) }}<div class="text-muted" *ngIf="n.contact_name">{{ n.contact_name }}</div></span>
							</td>
							<td data-label="Outcome">{{ label(outcomes, n.outcome) }}</td>
							<td data-label="Promised" class="is-num">
								<span *ngIf="n.promise_amount > 0 || n.promise_date">
									{{ n.promise_amount | currency }}
									<div class="text-muted" *ngIf="n.promise_date">by {{ n.promise_date | date:'MMM d' }}</div>
								</span>
								<span *ngIf="!(n.promise_amount > 0 || n.promise_date)" class="text-muted">—</span>
							</td>
							<td data-label="Follow-up">
								<span *ngIf="n.followup_date">{{ n.followup_date | date:'MMM d' }}</span>
								<span *ngIf="!n.followup_date" class="text-muted">—</span>
							</td>
							<td data-label="Doc">
								<!-- Every pinned document, not just the one copied onto the
								     note header: a single contact can cover several invoices.
								     Named, because a column of bare transnos can't be read. -->
								<div class="collection-notes__doc" *ngFor="let d of n.doc_pins">
									{{ d.typename || 'Doc' }} {{ d.transno }}
									<span class="text-muted" *ngIf="d.settled">settled</span>
								</div>
								<span *ngIf="!n.doc_pins?.length && n.transno > 0">{{ n.transno }}</span>
								<span *ngIf="!n.doc_pins?.length && !(n.transno > 0)" class="text-muted">account</span>
							</td>
							<td data-label="Note">{{ n.note }}</td>
							<td data-label="By">{{ n.userid }}</td>
							<td data-label="Status">
								<app-status-pill
									[status]="isBroken(n) ? 'failed' : statusPill(n.status)"
									[label]="isBroken(n) ? 'promise broken' : n.status">
								</app-status-pill>
							</td>
							<td>
								<button mat-icon-button (click)="openSheet(null, n)" aria-label="Open this note in the collections flyout" matTooltip="Open in flyout">
									<i class="material-icons">open_in_full</i>
								</button>
							</td>
						</tr>
						<tr *ngIf="!notes.length">
							<td colspan="10" class="text-center">
								<span class="feature-empty" [ngClass]="overdue > 0 ? 'is-failed' : ''">
									<i class="material-icons" aria-hidden="true">{{ overdue > 0 ? 'notifications_active' : 'history' }}</i>
									No collection contacts logged yet<span *ngIf="overdue > 0"> — and {{ overdue | currency }} is past due</span>
								</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Open items -->
			<div class="feature-section mt-4">
				<p class="feature-section__title">Open documents</p>
				<table class="feature-table feature-table--responsive">
					<thead>
						<tr>
							<th>Type</th>
							<th>Doc #</th>
							<th>Reference</th>
							<th>Dated</th>
							<th>Due</th>
							<th class="is-num">Total</th>
							<th class="is-num">Open</th>
							<th>Age</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let i of openItems">
							<td data-label="Type">{{ i.typename }}</td>
							<td data-label="Doc #">{{ i.transno }}</td>
							<td data-label="Reference">{{ i.reference }}</td>
							<td data-label="Dated">{{ i.trandate | date:'MMM d, y' }}</td>
							<td data-label="Due">{{ i.duedate | date:'MMM d, y' }}</td>
							<td data-label="Total" class="is-num">{{ i.doc_total | currency }}</td>
							<td data-label="Open" class="is-num">{{ i.balance | currency }}</td>
							<td data-label="Age">
								<app-status-pill
									[status]="i.days_overdue > 60 ? 'failed' : (i.days_overdue > 0 ? 'partial' : 'settled')"
									[label]="i.days_overdue > 0 ? (i.days_overdue + 'd · ' + bucketOf(i.days_overdue)) : 'current'">
								</app-status-pill>
							</td>
							<td>
								<button mat-stroked-button color="accent" [disabled]="!installed" (click)="openSheet(i)">Log</button>
							</td>
						</tr>
						<tr *ngIf="!openItems.length">
							<td colspan="9" class="text-center">
								<span class="feature-empty is-settled">
									<i class="material-icons" aria-hidden="true">check_circle</i>
									Nothing open — this account is clean
								</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

	</mat-card-content>
</mat-card>

<app-collection-sheet
	*ngIf="sheet"
	[data]="sheet"
	(closed)="onSheetClosed($event)">
</app-collection-sheet>
